// Appointments tab of the patient record (design/screens/patient-record.html "Appointments"; UC-APPOINTMENTS-007 step 5, PAT-17):
// every appointment the patient has, newest first — `GET /appointments?patientId=…&sort=startAt:desc` with no date range,
// keyset-paged — with the date and time, branch, type, who sees the patient ("Seen by", as on Today and the calendar), status
// and source, each row opening that day on the calendar.
// The branch follows BR-APPOINTMENTS-012: the acting branch by default, another branch the user acts in, or All branches for an
// Owner or Admin; the choice lives in the URL. Reading appointments writes no patient access entry (TEN-62). Slots are booked,
// moved and cancelled in Zoho Bookings (D23, D53); nothing here writes.
import type { AppointmentSource, AppointmentView, PatientView } from "@visionx/contracts";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { appointments } from "../api/endpoints.appointments";
import { isApiError } from "../api/problem";
import { formatDate, formatTime } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { Empty, ErrorAlert, Loading, Pager, Panel, Select } from "../ui/bits";
import { ApptStatusChip } from "./AppointmentsBits";
import { NoAccessPanel } from "./common";
import { patientName, useBranchNames } from "./PatientsShared";

const PAGE = 25;
const SOURCE_LABEL: Record<AppointmentSource, string> = { bookings: "Zoho Bookings", walk_in: "Walk-in", staff: "Staff", online: "Online" };
/** BR-APPOINTMENTS-012: an Owner or Admin may read every branch at once (`branchId=all`); anyone else reads a branch they act in. */
const readsAllBranches = (role: string | undefined): boolean => role === "Owner" || role === "Admin";
/** The branch-local calendar date of an appointment (`localStartAt` carries the branch's offset), for the calendar link. */
const branchDay = (a: AppointmentView): string => a.localStartAt.slice(0, 10);

export function PatientsAppointments({ p }: { p: PatientView }) {
  const s = useSessionView();
  const { name } = useBranchNames();
  const [params, setParams] = useSearchParams();
  const all = readsAllBranches(s?.user.role);
  const branchParam = params.get("branchId");
  const branchId = branchParam && s?.switchableBranches.some((b) => b.id === branchParam) ? branchParam : all ? "all" : (s?.activeBranchId ?? undefined);
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const permitted = can(s, "appointments.appointment.read");
  const q = useQuery({
    queryKey: ["patient", p.id, "appointments", branchId ?? "", cursor ?? ""],
    queryFn: () => appointments.list({ patientId: p.id, sort: "startAt:desc", branchId, cursor, limit: PAGE, count: true }),
    enabled: permitted && !!branchId,
  });
  if (!permitted) return <NoAccessPanel page="this patient's appointments" error={null} />;
  if (q.isError && isApiError(q.error) && q.error.code === "PERMISSION_DENIED") return <NoAccessPanel page="this patient's appointments" error={q.error} />;
  const rows = q.data?.data.items ?? [];
  const total = q.data?.data.totalCount;
  const first = cursors.length * PAGE + 1;
  const branchLabel = branchId === "all" ? "All branches" : name(branchId) ?? "";
  const choose = (v: string) => {
    setCursors([]);
    setParams((prev) => { const n = new URLSearchParams(prev); if (v === (all ? "all" : s?.activeBranchId)) n.delete("branchId"); else n.set("branchId", v); return n; }, { replace: true });
  };
  const choices = s ? [...s.switchableBranches.map((b) => ({ id: b.id, name: b.name })), ...(all ? [{ id: "all", name: "All branches" }] : [])] : [];
  return (
    <Panel title="Appointments" meta={`${branchLabel} · newest first${total !== undefined && rows.length > 0 ? ` · ${first}–${first + rows.length - 1} of ${total}` : ""}`} tight
      head={choices.length > 1 && <Select boxClass="right" aria-label="Branch" value={branchId ?? ""} onChange={(e) => choose(e.target.value)}>{choices.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</Select>}
      foot={<>
        <span className="help">Booked, moved and cancelled in Zoho Bookings; a walk-in is recorded on Today.</span>
        {q.data && (cursors.length > 0 || q.data.data.nextCursor) && <Pager label={`Page ${cursors.length + 1}`} hasPrev={cursors.length > 0} hasNext={!!q.data.data.nextCursor} onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, q.data!.data.nextCursor!])} />}
      </>}>
      {!branchId && <div className="panel-body"><span className="help">Choose a branch in the top bar to see this patient's appointments there.</span></div>}
      {q.isPending && q.fetchStatus !== "idle" && <div className="panel-body"><Loading what="appointments" /></div>}
      {q.isError && <div className="panel-body"><ErrorAlert error={q.error} /></div>}
      {q.data && rows.length === 0 && (
        <Empty title={`No appointments for ${patientName(p)}${branchId === "all" ? "" : ` at ${branchLabel}`}.`}>
          {branchId !== "all" && choices.length > 1 ? "Choose another branch, or book in Zoho Bookings; the booking appears after the next pull." : "Book in Zoho Bookings; the booking appears after the next pull."}
        </Empty>
      )}
      {rows.length > 0 && (
        <div className="tbl-scroll">
          <table>
            <thead><tr><th>When</th><th>Branch</th><th>Type</th><th>Seen by</th><th>Status</th><th>Source</th></tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id}>
                  <td className="nw"><Link to={`/appointments?date=${branchDay(a)}&branchId=${a.branchId}`} title="Open this day on the calendar">{formatDate(a.startAt)}</Link> <span className="mono">{formatTime(a.startAt)}–{formatTime(a.endAt)}</span></td>
                  <td>{name(a.branchId) ?? <span className="faint">—</span>}</td>
                  <td>{a.appointmentType.name}</td>
                  <td>{a.optometrist?.displayName ?? <span className="faint">Unassigned</span>}</td>
                  <td><ApptStatusChip status={a.status} />{a.cancellationReason && <div className="small faint">{a.cancellationReason}</div>}{a.awaitingApproval && <div className="small faint">awaiting approval in Bookings</div>}</td>
                  <td>{SOURCE_LABEL[a.source]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
